<!--
//nuxt-ui/components/pages/region-squad-simulator/CardPlayerSquadSimulator.vue
-->
<script setup lang="ts">
import type { Team } from "~/src/team/domain/models/Team";
import type { Player } from "~/src/player/domain/Player";
import type { AnyDraftPlayer } from "~/src/draft/domain/models/DraftPlayer";
import { useImages } from "~/nuxt-ui/Shared/composables/images";
import { setAliasUrl } from "~/src/Shared/utils";
import InjuredPlayerIcon from "~/nuxt-ui/components/Shared/icons/InjuredPlayerIcon.vue";
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";

type Props = {
  player: Player | AnyDraftPlayer;
  team: Team | undefined;
  loadingLazy?: boolean;
  isPlaceholder?: boolean;
  isOriginalPlayer?: boolean;
  isExchangeTeam?: boolean;
  isSelectedForExchange?: boolean;
  isSelectable?: boolean;
  isDownload?: boolean;
  showResetButton?: boolean;
  showRecruitButton?: boolean;
  isDraftSimulator?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "sell-player", player: Player): void;
  (e: "select-for-exchange", player: Player): void;
  (e: "reset-player", player: Player): void;
  (e: "recruit-player", player: Player): void;
}>();

const { playerPlaceholderPath } = useImages();
const { isBrazil } = useRegions();

const defaultColor = "#888888";
const aliasUrlPlayerImage =
  "/_ipx/s_256x360/" + setAliasUrl(props.isDraftSimulator ? props.player.imageUrl : props.player.image.url);

function handleSell() {
  if (!props.isExchangeTeam) {
    emit("sell-player", props.player);
  }
}

function handleRecruit() {
  if (props.isExchangeTeam && props.isSelectable) {
    emit("recruit-player", props.player);
  }
}

function handleExchange() {
  if (!props.isExchangeTeam) {
    emit("select-for-exchange", props.player);
  }
}

function handleReset() {
  if (!props.isExchangeTeam) {
    emit("reset-player", props.player);
  }
}

const firstColorHex =
  isBrazil &&
  ((props.team?.id === 49 && props.team?.logo.url?.includes("669107952.png")) ||
    (props.team?.id === 50 && props.team?.logo.url?.includes("751656156.png")))
    ? defaultColor
    : props.team?.firstColorHEX
      ? props.team?.firstColorHEX
      : defaultColor;
</script>

<template>
  <div
    :class="[
      'player-card-container',
      {
        'opacity-50': isExchangeTeam && !isSelectable,
        '!border-primary': isSelectedForExchange,
        'cursor-pointer hover:scale-105': isSelectable,
        'w-[200px]': isDownload,
      },
    ]"
    @click="isExchangeTeam && isSelectable && handleRecruit()"
  >
    <div class="h-full absolute z-[3]">
      <div
        class="clip-diagonal-right"
        :style="`background-image: linear-gradient(240deg, ${
          team ? firstColorHex : defaultColor
        }99 40%, #141414 85%);`"
      ></div>
    </div>

    <div class="player-card-content">
      <div class="container-player-img">
        <NuxtImg
          v-if="!isDownload"
          :src="
            player.image && player.image.url
              ? setAliasUrl(player.image.url)
              : isDraftSimulator && player.imageUrl
                ? setAliasUrl(player.imageUrl)
                : playerPlaceholderPath
          "
          :alt="isDraftSimulator ? player.name : player.shortName || ''"
          :loading="loadingLazy ? 'lazy' : 'eager'"
          height="642"
          width="456"
          sizes="122px sm:128px"
        />
        <img
          v-else
          :src="
            (player.image && player.image.url) || (isDraftSimulator && player.imageUrl)
              ? aliasUrlPlayerImage
              : playerPlaceholderPath
          "
          :alt="isDraftSimulator ? player.name : player.shortName"
          height="642"
          width="456"
        />
        <div v-if="player.jersey" class="player-number">#{{ player.jersey }}</div>
        <div
          v-if="player?.metaInformation?.injury && player?.metaInformation?.injury === 'true'"
          class="absolute top-12 left-2"
        >
          <injured-player-icon :width="20" :height="20" />
        </div>
        <div class="market-value-container" v-if="!isPlaceholder">
          <p class="market-value-text">
            <span v-if="player.marketValue && player.marketValue > 0"> {{ player.marketValue }}M </span>
            <span v-else-if="player.macros && player.macros.average"> {{ player.macros.average }} </span>
            <span v-else> - </span>
          </p>
        </div>
      </div>

      <div class="w-full min-h-full flex flex-col md:justify-between">
        <div class="container-player-info">
          <div class="container-player-info-text">
            <div>
              <h3 class="player-name">
                <span v-if="isDraftSimulator && player.name">
                  {{ player.name }}
                </span>
                <span v-else-if="player.shortName">
                  {{ player.shortName }}
                </span>
                <span v-else>{{ $t("squadSimulator.placeholder.shortName") }} </span>
              </h3>
              <p class="player-role">
                <span v-if="isDraftSimulator && player.role">
                  {{ $t(`draft.player.position.${player.role}`) }}
                </span>
                <span v-else-if="player.role">
                  {{ $t(`${player.role}`) }}
                </span>
                <span v-if="!player.role && isPlaceholder">
                  {{ $t("squadSimulator.placeholder.role") }}
                </span>
              </p>
            </div>
            <div v-if="!isExchangeTeam && !isDownload && !isDraftSimulator" class="container-player-buttons">
              <button v-if="!isPlaceholder" @click="handleSell" class="btn btn-primary">
                {{ $t("squadSimulator.button.sell") }}
              </button>
              <button
                v-if="isPlaceholder && showRecruitButton"
                @click="handleExchange"
                :class="['btn', isSelectedForExchange ? 'btn-disabled' : 'btn-emphasis']"
              >
                {{ $t("squadSimulator.button.recruit") }}
              </button>
              <button
                v-if="!isOriginalPlayer && showResetButton"
                @click="handleReset"
                class="btn btn-default"
              >
                {{ $t("squadSimulator.button.reset") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.player-card-container {
  @apply rounded-lg bg-gray-900 border-2 border-transparent overflow-hidden relative transition-all ease-in-out duration-200;
}

.clip-diagonal-right {
  @apply h-full w-[210px];
  clip-path: polygon(0 0, 100% 0%, -40% 100%, 0% 100%);
}

.player-card-content {
  @apply h-full max-w-min p-3 relative z-[5];
}

.container-player-info {
  @apply mt-2.5;
}

.player-number {
  @apply text-sm font-bold px-2 py-1 bg-gray-800 rounded-full bg-opacity-60 absolute bottom-1 right-1 z-[3];
}

.container-player-img {
  @apply shrink-0 relative rounded bg-[url('/img/bg-card/bg-card-player-image.jpg')] bg-cover bg-center bg-no-repeat min-w-44 max-w-44 aspect-square overflow-hidden;
}

.container-player-img::after {
  content: "";
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 51.94%);
  @apply absolute bottom-0 w-full h-20 z-[2];
}

.container-player-img img {
  @apply w-full h-full object-top object-cover;
}

.player-name {
  @apply font-bold text-sm max-w-[176px] overflow-hidden text-ellipsis whitespace-nowrap;
}

.player-role {
  @apply text-gray-400 text-sm leading-[21px] first-letter:capitalize;
}

.market-value-container {
  @apply rounded-lg py-1 px-2 bg-primary !text-black ml-2 absolute top-2 left-0;
}

.market-value-text {
  @apply text-base font-bold;
}

.container-player-buttons {
  @apply flex flex-row justify-center gap-2 pt-2 max-w-[176px];
}

.container-player-buttons button {
  @apply flex-grow basis-[calc(50%-0.25rem)] !px-1.5;
}
</style>
